import angular from 'angular';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';

/**
 * Factorizes code that initialises the scope of controllers which use the site filter directive
 */
export class SiteFilterHelperService {

  constructor(LocalStorageFactory, StrategiesFactory, DropdownItemsFactory) {
    this.LocalStorageFactory = LocalStorageFactory;
    this.StrategiesFactory = StrategiesFactory;
    this.DropdownItemsFactory = DropdownItemsFactory
  }

  /**
   * Initialises the scope of a controller that will use the <site-filter> directive, by doing the following:
   *  - Inits $scope.sites with a copy of the given sites array, sorted by site name and site type, 
   *    with a .icon attribute equal to site.type_name
   *  - Adds a $watch to the given $scope that will update $scope.site 
   *    when $scope.filteredSiteId is updated (presumably by a <site-filter> directive)
   *  - Inits $scope.filteredSiteId to a given value that has higher priority (if any is passed), 
   *    or to a value retrieved from localstorage,
   *    or to the first item of the site list (or the special "all-site" item) if no value was found in the local storage
   */
  initScope($scope, sites, localStorageKey, higherPriorityInitialValue, hasAllSitesItem) {
    $scope.sites = angular.copy(sites);
    this.addSortKeysAndSort($scope.sites);
    $scope.$watch("filteredSiteId", (newSiteId) => {
      if (localStorageKey) {
        this.LocalStorageFactory.setItem(localStorageKey, newSiteId);
      }
      $scope.site = sites.filter(s => s.id === newSiteId)[0];
    });
    const savedFilteredSiteId = localStorageKey ? this.LocalStorageFactory.getItem(localStorageKey) : null;
    if (typeof higherPriorityInitialValue === 'number') {
      // someone set the site id via a state param; let's use that
      $scope.filteredSiteId = higherPriorityInitialValue;
    } else if (typeof savedFilteredSiteId === 'number') {
      // use local storage value if present
      $scope.filteredSiteId = savedFilteredSiteId;
    } else {
      // default to first site in sorted list, 
      // or to "All Sites" if the scope we are initializing will offer it
      const firstSiteId = $scope.sites.length > 0 ? $scope.sites[0].id : 0;
      $scope.filteredSiteId = hasAllSitesItem ? 0 : firstSiteId;
    }
  }

  /**
   * Adds a couple of site-related attributes to all objects of the given array.
   * Theses items must have a site_id property
   */
  addNameIconAndSortKeyToItems(items, sites) {
    if (items && items.forEach) {
      items.forEach(item => {
        const site = sites.find(s => s.id === item.site_id);
        if (site) {
          item.siteName = site.name;
          item.siteIcon = site.type_name;
          item.sortKey = `${site.sortKey}#${this.zeroPad(item.id)}`;
        }
      });
    }
  }

  /**
   * Returns the subset of the given sites for which the given user has the given capability
   */
  filterSitesThatHaveCapability(sites, loggedUser, capabilityId) {
    if (!loggedUser.capabilities) {
      return [];
    }
    return (sites || []).filter(({ id }) => loggedUser.capabilities[id]?.includes(capabilityId));
  }


  /* 
    Adds a 'sortKey' field to each item of the given site submenu
    This is called by both menu.controller and parameters.controller
    (parameters.controller builds and displays a site list of its own as per nav2018-step1 )
   */
  addSortKeysAndSort(sites) {
    let currentSortIndex = 0;
    angular.forEach(sites, (site, siteIndex) => {

      // Consecutive items with a social network type_name need to have the same currentSortIndex,
      // so that they can be sorted by name and type_name
      // On the other hand items without an type_name need to have a different currentSortIndex
      // so that they appear at the same position as in the input array
      // (there is sometimes an additional 'all sites' item at the beginning, 
      //  which doesn't have a social network type_name)
      if (!site.type_name) {
        currentSortIndex = siteIndex;
      }
      let sortKey = this.zeroPad(currentSortIndex);
      if (site.type_name) {
        const networksOrder = this.StrategiesFactory.getOrderedSiteTypeStrings();
        const networkIndex = networksOrder.indexOf(site.type_name);
        const upperCaseSiteName = site.name ? site.name.toUpperCase() : site.name;
        sortKey += `#${upperCaseSiteName}#${this.zeroPad(networkIndex)}`;
      }
      site.sortKey = sortKey;
    });
    sites.sort((a, b) => {
      if (a.sortKey < b.sortKey) {
        return -1;
      }
      if (a.sortKey > b.sortKey) {
        return 1;
      }
      return 0;
    });
  }

  // A quick and naive zero-padding algo so that we can handle more than 10 non-account elements
  // and more than 10 social networks.
  // When IE need not be supported any more, the following method can be can used instead:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padS#Browser_compatibilitytart
  zeroPad(i) {
    return (`000${i}`).slice(-3);
  }


  buildAllowedSitesAndSiteGroupsListItems(sites, siteGroups, sitesMap, loggedUser) {
    const siteGroupsFiltered = siteGroups.filter(siteGroup => {
      const sitesNb = Object.keys(siteGroup.sites).length;
      if (!sitesNb) {
        return false;
      }
      const sitesFromMap = Object.keys(siteGroup.sites).map(key => sitesMap.get(+key)).filter(i => i);
      const sitesFound = this.filterSitesThatHaveCapability(sitesFromMap, loggedUser, CapabilityIdEnum.Answer);
      return sitesNb === sitesFound.length;
    });
    const sitesNotInGroup = sites.filter(site => !site.site_group && !site.site_group_id)

    const sortItems = (a, b) => {
      let result = a.type - b.type;
      if (!result) {
        return a.sortKey.localeCompare(b.sortKey)
      }
      return result;
    };
    const siteIds = this.DropdownItemsFactory.buildSiteItems(sitesNotInGroup || []).sort(sortItems);
    const siteGroupIds = this.DropdownItemsFactory.buildSiteGroupItems(siteGroupsFiltered || []).sort(sortItems);
    const itemsMap = new Map();
    const aggregatedItems = [...siteGroupIds, ...siteIds]
      .map(item => {
        const result = { ...item, key: `${item.isGroup ? 'siteGroup' : 'site'}|${item.id}` };
        itemsMap.set(result.key, result);
        return result
      });
    return {
      sites: aggregatedItems,
      itemsMap
    };
  }

  extractSitesAndSiteGroupsByKey(keys) {
    const siteIds = [];
    const siteGroupIds = [];
    (keys || []).forEach(key => {
      if (/site\|/.test(key)) {
        siteIds.push(+key.replace('site|', ''));
      } else if (/siteGroup\|/.test(key)) {
        siteGroupIds.push(+key.replace('siteGroup|', ''));
      }
    });
    return { siteIds, siteGroupIds };
  }

  extractSitesAndSiteGroups(sites) {
    const keys = sites.map(({ key }) => key);
    return this.extractSitesAndSiteGroupsByKey(keys)
  }

  convertSiteAndSiteGroupsToKey(siteIds, siteGroupIds) {
    const siteKeys = (siteIds || []).map(id => `site|${id}`);
    const sitesGroupKeys = (siteGroupIds || []).map(id => `siteGroup|${id}`);
    return [...sitesGroupKeys, ...siteKeys];
  }

  static SiteFilterHelperFactory(LocalStorageFactory, StrategiesFactory, DropdownItemsFactory) {
    return new SiteFilterHelperService(LocalStorageFactory, StrategiesFactory, DropdownItemsFactory);
  }
}
SiteFilterHelperService.SiteFilterHelperFactory.$inject = ['LocalStorageFactory', 'StrategiesFactory', 'DropdownItemsFactory'];

